import { useState } from "react";
import {
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  CircularProgress,
  IconButton,
  Typography,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Person as PersonIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSession, useLogout } from "../../features/auth";
import { useMasterData } from "../../hooks/useMasterData";

export const UserMenu: React.FC = () => {

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { data: session, isLoading } = useSession();
  const logoutMutation = useLogout();
  const { clearMasterData } = useMasterData();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    try {
      await logoutMutation.mutateAsync();
      clearMasterData();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  const user = session?.user;

  return (
    <>
      <IconButton onClick={handleClick}>
        <AccountCircleIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Avatar>
              <PersonIcon />
            </Avatar>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography variant="subtitle2" noWrap sx={{ mb: -0.5 }}>
                {user?.full_name || user?.username || "User"}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.email || ""}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider />
        <MenuItem
          onClick={handleLogout}
          disabled={logoutMutation.isPending}
          sx={{ py: 1.5 }}
        >
          <ListItemIcon>
            {logoutMutation.isPending ? (
              <CircularProgress size={20} color="primary" />
            ) : (
              <LogoutIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {logoutMutation.isPending ? "Logging out..." : "Logout"}
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
